import { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';

import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';

import { DragModal, DragModalHeader } from 'components/DragModal/DragModal';
import { useExportImport } from 'components/ExportImport/ExportImportContext';
import { useDashboardContext } from 'screens/dashboard/Dashboard';
import { ApiErrorResponse, TWidget } from 'utility/types/dataProvider';
import { normalizeDashboardFiltersQuery } from '../shared/utils';
import WidgetAlertButton from './WidgetAlertButton';
import WidgetAlerts from './WidgetAlerts';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { hasBetaProgram } from 'utility/experiments';
import { useWidgetAlertPulse, ALERT_PULSE_ANIMATION } from './useWidgetAlertPulse';
import { useWidgetContext } from '../Widget';
import WidgetAppliedFilters from './WidgetAppliedFilters';
import { useIsCustomDashboard } from '../hooks/useIsCustomDashboard';

type ExportCsvParams = {
  widget: TWidget;
  filters?: Record<string, any>; // Dashboard filters object (can include pagination, sorting, etc.)
  rows?: number;
  isTableWidget: boolean;
  isAssetDashboard?: boolean;
  assetUUID?: string;
  exportAll?: boolean;
};

// Custom hook for CSV export using TopNav context
const useCsvExport = (isCustomDashboard: boolean, onSuccess?: () => void) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { t } = useTranslation();
  const {
    export: { addExport },
  } = useExportImport();

  const exportCsv = async (params: ExportCsvParams) => {
    const {
      widget,
      filters,
      rows,
      isTableWidget,
      isAssetDashboard,
      assetUUID,
      exportAll,
    } = params;

    // Check if "All Rows" is selected.
    const shouldExportAllRows = rows === -1 || rows === undefined;

    // Separate pagination params from other filters to ensure they're only included when not exporting all rows.
    const { page, pageSize, ...restFilters } = filters || {};

    // Post CSV export request.
    // When exportAll is true (e.g. community reports), send no filters to export all data.
    const posted = await dataProvider.createWidgetExport({
      id: widget?.id,
      payload: exportAll
        ? {}
        : normalizeDashboardFiltersQuery(
            {
              ...restFilters,
              // Only include pagination params when not exporting all rows
              ...(!shouldExportAllRows && {
                pageSize: isTableWidget ? rows : pageSize || 10,
                ...(page !== undefined && { page }),
              }),
              ...(isAssetDashboard && assetUUID && { assetUUID }),
            },
            widget,
            isCustomDashboard,
          ),
    });

    const taskId = posted?.taskID || '';
    if (!taskId) throw new Error('No taskId found');

    // Add export to TopNav context for tracking in DownloadMenu.
    addExport(taskId, 'dashboard', {
      id: widget?.id,
      title: widget?.title,
    });

    return { taskId };
  };

  const mutation = useMutation<{ taskId: string }, ApiErrorResponse, ExportCsvParams>({
    mutationFn: exportCsv,
    onSuccess: () => {
      notify?.(
        t('blox.dashboards.exports.export_started') ||
          'Dashboard export started. Check the download menu for progress.',
        {
          type: 'info',
        },
      );
      // Close the modal after successful export
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error starting CSV export', { error });
      notify?.(t('blox.dashboards.exports.error'), { type: 'error' });
    },
  });

  return mutation;
};

type TWidgetActionsProps = {
  allowExpand: boolean;
  isHidden: boolean;
  setIsExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
  hideAlertButton?: boolean;
};

/**
 * Actions for the widget, located in the upper right corner.
 * @param props TWidgetActionsProps
 * @returns void
 */
const WidgetActions = ({
  allowExpand,
  isHidden,
  setIsExpanded,
  hideAlertButton = false,
}: TWidgetActionsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { widget, chartType } = useWidgetContext();
  const { app, filters, isAssetDashboard, assetUUID, hasProDataInsights } =
    useDashboardContext();
  const isCustomDashboard = useIsCustomDashboard();
  const { sessionConfig, userInfo } = useCosmos();
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [miniMenuAnchorEl, setMiniMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isAlertDrawerOpen, setIsAlertDrawerOpen] = useState<boolean>(false);

  const isMiniMenuOpen = Boolean(miniMenuAnchorEl);
  const hasBetaAccess = hasBetaProgram(sessionConfig, userInfo);
  const isAlertPulsing = useWidgetAlertPulse(widget?.id);
  const showAlerts = hasProDataInsights && hasBetaAccess && !hideAlertButton;

  // React Hook Form setup
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      rows: -1, // Default export format = "All Rows"
    },
  });

  const exportRows = watch('rows');
  const isTableWidget = chartType === 'table';
  const isKpi = chartType === 'kpi';
  // Community reports dashboards export all data without row selection or filters.
  const isExportAll = app === 'subscription.reports';
  const showRowSelector = isTableWidget && !isExportAll;

  const handleOpenMiniMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMiniMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMiniMenu = () => {
    setMiniMenuAnchorEl(null);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    handleCloseMiniMenu();
  };

  // Use the custom CSV export hook with modal close callback
  const csvExportMutation = useCsvExport(isCustomDashboard, handleCloseExportModal);

  const handleExportCsv = async (formData?: { rows: number }) => {
    csvExportMutation.mutate({
      widget,
      filters,
      rows: formData?.rows ?? exportRows,
      isTableWidget,
      isAssetDashboard,
      assetUUID,
      exportAll: isExportAll,
    });
  };

  // Hide the entire actions area if isHidden is true.
  if (isHidden) return null;

  return (
    <Box
      data-testid={`widget-actions--${widget.id}`}
      sx={{
        display: 'flex',
        gap: 0,
        alignItems: 'center',
        flexDirection: 'row',
      }}
    >
      {allowExpand && (
        <IconButton
          data-testid={`widget-expand-button--${widget?.id}`}
          size="small"
          onClick={() => setIsExpanded?.(true)}
        >
          <OpenInFullRoundedIcon fontSize="small" />
        </IconButton>
      )}
      <Stack
        data-testid={`widget-actions-stack--${widget?.id}`}
        sx={{
          alignItems: 'start',
          flexDirection: 'row',
        }}
      >
        {/* Desktop: Separate Buttons */}
        {!isMobile && (
          <>
            {/* Desktop Alert Button */}
            <WidgetAlertButton hideAlertButton={hideAlertButton} />

            {/* Desktop Export Button */}
            <IconButton
              data-testid={`widget-export-button--${widget?.id}`}
              size="small"
              onClick={handleOpenMiniMenu}
              sx={{
                ml: -1,
                py: isKpi ? 0 : undefined,
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </>
        )}

        {/* Mobile: Unified Menu Button */}
        {isMobile && (
          <IconButton
            data-testid={`widget-actions-menu-button-mobile--${widget?.id}`}
            size="small"
            onClick={handleOpenMiniMenu}
            sx={{
              animation: showAlerts && isAlertPulsing ? ALERT_PULSE_ANIMATION : 'none',
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Applied Filters. */}
      <WidgetAppliedFilters />

      {/* Desktop Export Mini Menu */}
      {!isMobile && (
        <Menu
          data-testid={`widget-export-mini-menu--${widget?.id}`}
          disableEnforceFocus={true}
          anchorEl={miniMenuAnchorEl}
          open={isMiniMenuOpen}
          onClose={handleCloseMiniMenu}
        >
          <MenuItem onClick={() => setIsExportModalOpen(true)}>
            {t('blox.dashboards.exports.button')}
          </MenuItem>
        </Menu>
      )}

      {/* Mobile Unified Menu */}
      {isMobile && (
        <Menu
          data-testid={`widget-actions-menu-mobile--${widget?.id}`}
          disableEnforceFocus={true}
          anchorEl={miniMenuAnchorEl}
          open={isMiniMenuOpen}
          onClose={handleCloseMiniMenu}
        >
          {/* Alert Menu Item */}
          {showAlerts && (
            <MenuItem
              onClick={() => {
                setIsAlertDrawerOpen(true);
                handleCloseMiniMenu();
              }}
            >
              <NotificationsNoneOutlinedIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              {t('blox.dashboards.alerts.label')}
            </MenuItem>
          )}

          {/* Export Menu Item */}
          <MenuItem onClick={() => setIsExportModalOpen(true)}>
            {t('blox.dashboards.exports.button')}
          </MenuItem>
        </Menu>
      )}

      {/* Alert Drawer (Mobile) */}
      {isMobile && showAlerts && (
        <WidgetAlerts
          open={isAlertDrawerOpen}
          onClose={() => setIsAlertDrawerOpen(false)}
          widget={widget}
        />
      )}

      {/* Export Dialog */}
      <DragModal
        disableEnforceFocus={true}
        open={isExportModalOpen}
        onClose={handleCloseExportModal}
        initialHeight={280}
        maxHeight={280}
        initialWidth={400}
        maxWidth={400}
        header={<DragModalHeader text={t('blox.dashboards.exports.title')} />}
        body={
          <Box
            data-testid={`widget-export-modal-body--${widget?.id}`}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Typography>
              {isExportAll
                ? t('blox.dashboards.exports.description_all')
                : t('blox.dashboards.exports.description')}
            </Typography>

            {showRowSelector && (
              <FormControl
                fullWidth
                data-testid="export-format-formcontrol"
                size="small"
              >
                <InputLabel id="export-format-label">
                  {t('blox.dashboards.exports.table_field.label')}
                </InputLabel>
                <Controller
                  name="rows"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      data-testid="export-format-select"
                      labelId="export-format-label"
                    >
                      <MenuItem value={-1}>
                        {t('blox.dashboards.exports.table_field.all')}
                      </MenuItem>
                      <MenuItem value={25}>
                        {t('blox.dashboards.exports.table_field.25')}
                      </MenuItem>
                      <MenuItem value={50}>
                        {t('blox.dashboards.exports.table_field.50')}
                      </MenuItem>
                      <MenuItem value={100}>
                        {t('blox.dashboards.exports.table_field.100')}
                      </MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            )}
          </Box>
        }
        footer={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              data-testid="export-modal-button"
              variant="contained"
              onClick={handleSubmit(handleExportCsv)}
              disabled={csvExportMutation.isPending}
              startIcon={
                csvExportMutation.isPending ? (
                  <CircularProgress
                    data-testid="export-modal-button-loading"
                    size={20}
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'light'
                          ? theme.blox.colors.offwhite
                          : theme.blox.colors.offblack,
                    })}
                  />
                ) : undefined
              }
            >
              {isTableWidget
                ? t('blox.dashboards.exports.download')
                : t('blox.dashboards.exports.button')}
            </Button>
          </Box>
        }
      />
    </Box>
  );
};

export default WidgetActions;
